import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Mail, MessageSquare, CheckCircle2, AlertCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ContactPage() {
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axios.post(`${API}/contact`, form, {
        headers: { 'Content-Type': 'application/json' }
      });
      setSent(true);
    } catch (err) {
      setError("Une erreur est survenue lors de l'envoi. Veuillez réessayer ou nous contacter directement par email.");
      console.error("Contact form error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col page-enter">
      <Seo
        title="Contact"
        description="Une question, un signalement ou une suggestion ? Contactez l'équipe Fiable ou Pas. Réponse sous 48h ouvrées."
        path="/contact"
      />
      <Header />
      <main className="flex-1 py-12 bg-[#F9FAFB]">
        <div className="container-app max-w-2xl">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A8A]">Nous contacter</span>
            <h1 className="text-3xl sm:text-4xl font-extrabold font-heading text-slate-900 mt-3 mb-4">Contact</h1>
            <p className="text-slate-600 text-sm leading-relaxed max-w-lg mx-auto">
              Une question sur nos analyses, un signalement ou une suggestion ? Nous vous répondons dans les meilleurs délais.
            </p>
          </div>

          {sent ? (
            <div className="card-base p-10 text-center" data-testid="contact-success">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-800 mb-2">Message envoyé</h2>
              <p className="text-slate-600 text-sm">
                Merci pour votre message. Nous vous répondrons dans les meilleurs délais.
              </p>
            </div>
          ) : (
            <div className="card-base p-8">
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Votre nom</label>
                    <input
                      type="text"
                      required
                      disabled={loading}
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-slate-100 disabled:cursor-not-allowed"
                      placeholder="Jean Dupont"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                    <input
                      type="email"
                      required
                      disabled={loading}
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-slate-100 disabled:cursor-not-allowed"
                      placeholder="vous@exemple.fr"
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Sujet</label>
                  <select
                    required
                    disabled={loading}
                    value={form.subject}
                    onChange={e => setForm({ ...form, subject: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-700 disabled:bg-slate-100 disabled:cursor-not-allowed"
                    data-testid="contact-subject"
                  >
                    <option value="">Sélectionnez un sujet</option>
                    <option value="analyse">Question sur une analyse</option>
                    <option value="signalement">Signaler un problème</option>
                    <option value="suggestion">Suggestion d'amélioration</option>
                    <option value="presse">Contact presse</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Message</label>
                  <textarea
                    required
                    disabled={loading}
                    rows={5}
                    value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none disabled:bg-slate-100 disabled:cursor-not-allowed"
                    placeholder="Décrivez votre question ou votre demande..."
                    data-testid="contact-message"
                  />
                </div>
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}
                <div className="text-xs text-slate-400">
                  En envoyant ce formulaire, vous acceptez que vos données soient utilisées uniquement pour répondre à votre demande.{" "}
                  <a href="/politique-de-confidentialite" className="underline hover:text-slate-600">Politique de confidentialité</a>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <MessageSquare className="w-4 h-4" />
                      Envoyer le message
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          <div className="mt-8 card-base p-6 text-center text-sm text-slate-600">
            <Mail className="w-5 h-5 text-slate-400 mx-auto mb-2" />
            <p>Vous pouvez également nous contacter directement à l'adresse :</p>
            <p className="font-semibold text-slate-800 mt-1">hello@fiableoupas.com</p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
